import round from "lodash/round";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";

const OZ_PER_L = 33.8140227;

function LogVitalsCard({ dailyStat, units = "metric" }) {
  const { t } = useTranslation(["athlete-portal", "common"]);

  function formatSleep(ms) {
    if (!ms) return null;
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const hAbbr = t("common:units.hourAbbr");
    const mAbbr = t("common:units.minuteAbbr");
    if (h && m)
      return t("calendar.cards.vitals.sleepHoursMinutes", {
        h,
        m,
        hAbbr,
        mAbbr,
      });
    if (h) return t("calendar.cards.vitals.sleepHours", { h, hAbbr });
    return t("calendar.cards.vitals.sleepMinutes", { m, mAbbr });
  }

  function formatWater(liters) {
    if (!liters) return null;
    if (units === "imperial") {
      return `${round(liters * OZ_PER_L, 0)} ${t("common:units.ozAbbr")}`;
    }
    return `${round(liters, 1)} ${t("common:units.literAbbr")}`;
  }

  if (!dailyStat) return null;
  const {
    sleep,
    steps,
    waterIntake,
    hrv,
    restingHeartRate,
    bodyFatPercentage,
  } = dailyStat;

  const rows = [
    {
      key: "sleep",
      icon: "/img/new-icons/sleep.svg",
      label: t("calendar.cards.vitals.sleep"),
      value: formatSleep(sleep),
    },
    {
      key: "steps",
      icon: "/img/new-icons/steps.svg",
      label: t("calendar.cards.vitals.steps"),
      value: steps != null && steps > 0 ? Number(steps).toLocaleString() : null,
    },
    {
      key: "water",
      icon: "/img/new-icons/drop-icon.svg",
      label: t("calendar.cards.vitals.water"),
      value: formatWater(waterIntake),
    },
    {
      key: "hrv",
      icon: "/img/new-icons/heart-pulse-solid.svg",
      useCurrentColor: true,
      label: t("calendar.cards.vitals.hrv"),
      value: hrv != null ? `${hrv}${t("common:units.msAbbr")}` : null,
    },
    {
      key: "restingHeartRate",
      icon: "/img/new-icons/heart-pulse-solid.svg",
      useCurrentColor: true,
      label: t("metrics.restingHeartRate"),
      value: restingHeartRate != null
        ? `${restingHeartRate} ${t("restingHeartRate.unit")}`
        : null,
    },
    {
      key: "bodyFatPercentage",
      icon: "/img/new-icons/percentage-square.svg",
      label: t("metrics.bodyFat"),
      value: bodyFatPercentage ? `${round(bodyFatPercentage, 1)}%` : null,
    },
  ].filter((r) => r.value != null);

  if (rows.length === 0) return null;

  return (
    <div className="log-card log-card--vitals">
      <div className="log-card__header">
        <SvgIcon
          src="/img/new-icons/heart-pulse-solid.svg"
          useCurrentColor={true}
        />
        {t("calendar.cards.vitals.title")}
      </div>
      <div className="log-card__body">
        {rows.map(({ key, icon, useCurrentColor, label, value }) => (
          <div key={key} className="log-card__stat-row">
            <SvgIcon src={icon} useCurrentColor={useCurrentColor} />
            <span className="log-card__stat-label">{label}</span>
            <span className="log-card__stat-value">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default LogVitalsCard;
